import * as Sentry from '@sentry/react';
import axios from 'axios';

import { ALERT_NO_LONGER_ACTIVE_HTTP_STATUSES } from './constants/device-alerts.js';
import {
  DEVICE_AVAILABILITY_EXPECTED_HTTP_STATUSES,
  DEVICE_AVAILABILITY_ROUTE,
} from './constants/device-availability.js';
import { SUPPRESSIONS_EXPORT_TOO_MANY_ROWS_ERROR_CODE } from './constants/suppressions.js';
import { getApiErrorCode } from './lib/error.js';
import { isBenignStopError } from './services/suppressions.js';

export const API_BASE = '/api/v1';

export const apiClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// `error.config.url` is the relative path passed to `apiClient.post` — baseURL is applied separately.
const BROADBAND_SUPPRESSION_STOP_ROUTE = /^\/devices\/[^/]+\/broadband_suppressions\/[^/]+\/stop$/;
const DEVICE_ALERT_ACKNOWLEDGE_ROUTE = /^\/alerts\/[^/]+\/acknowledge$/;
const SUPPRESSIONS_EXPORT_ROUTE = '/analytics/suppressions/export';

// Acking an already-acknowledged, resolved, or expired alert is routine on a stale card — the
// operator never sees it as a failure, so it must not page anyone either.
const isBenignAlertAcknowledgeError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  typeof error.config?.url === 'string' &&
  DEVICE_ALERT_ACKNOWLEDGE_ROUTE.test(error.config.url) &&
  (ALERT_NO_LONGER_ACTIVE_HTTP_STATUSES as readonly number[]).includes(error.response?.status ?? 0);

// The row cap can still trip at export time (see useSuppressionsExport); the operator gets a toast.
const isBenignSuppressionsExportError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.config?.url === SUPPRESSIONS_EXPORT_ROUTE &&
  getApiErrorCode(error) === SUPPRESSIONS_EXPORT_TOO_MANY_ROWS_ERROR_CODE;

// The panel shows these rejections as operator-facing text, so they are not failures to page on.
const isBenignDeviceAvailabilityError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.config?.url === DEVICE_AVAILABILITY_ROUTE &&
  (DEVICE_AVAILABILITY_EXPECTED_HTTP_STATUSES as readonly number[]).includes(
    error.response?.status ?? 0,
  );

// A `responseType: 'blob'` request gets its JSON error body as a Blob too; decode it so the error
// code is readable. An undecodable body is left as the Blob.
const decodeBlobErrorBody = async (error: unknown): Promise<void> => {
  if (!axios.isAxiosError(error) || !error.response) return;
  const { data } = error.response;
  if (!(data instanceof Blob) || !data.type.includes('json')) return;
  try {
    error.response.data = JSON.parse(await data.text());
  } catch {
    // keep the Blob
  }
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    await decodeBlobErrorBody(error);

    // Route check kept separate from `isBenignStopError`: gating on code alone would also absorb
    // an unrelated error that reused the same string, and the route check can't live inside that
    // helper — it's also called on the hook's re-thrown plain Error, which has no `config`.
    // Classify on `code`, never HTTP status: 400/404 on this route also mean device-offline/not-found.
    const hitBroadbandStopRoute =
      axios.isAxiosError(error) &&
      typeof error.config?.url === 'string' &&
      BROADBAND_SUPPRESSION_STOP_ROUTE.test(error.config.url);

    const isBenign =
      (hitBroadbandStopRoute && isBenignStopError(error)) ||
      isBenignAlertAcknowledgeError(error) ||
      isBenignSuppressionsExportError(error) ||
      isBenignDeviceAvailabilityError(error);

    if (!isBenign) {
      Sentry.captureException(error);
    }

    throw error;
  },
);

export default apiClient;
